import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import TelegramLoginButton from '../components/TelegramLoginButton';
import StaticLayout from '../layouts/StaticLayout';
import { Input } from '../components/Form/Input';
import { Loader } from '../components/Loader';

export default function AccountRecovery() {
  const defaultFormData = {
    email: '',
    invoiceNumber: '',
    last4Digits: '',
    botUsername: '',
    verificationCode: ''
  };
  const [step, setStep] = useState('captcha'); // captcha, form, verification, success
  const [requestId, setRequestId] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [telegramUserFirstName, setTelegramUserFirstName] = useState(null);
  const [telegramUserLastName, setTelegramUserLastName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTryAgain, setIsTryAgain] = useState(false);
  const [error, setError] = useState(null);

  const recaptchaRef = React.useRef();

  const onLogin = async (telegramUser) => {
    try {
      setIsLoading(true);
      setError(null);

      const token = recaptchaRef.current.getValue();
      recaptchaRef.current.reset();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/account-recovery/create-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          telegramUserId: telegramUser.id,
          first_name: telegramUser.first_name,
          last_name: telegramUser.last_name,
          username: telegramUser.username,
          captchaToken: token,
        }),
      });
      if (telegramUser.first_name) {
        setTelegramUserFirstName(telegramUser.first_name);
      }
      if (telegramUser.last_name) {
        setTelegramUserLastName(telegramUser.last_name);
      }

      // const response = await new Promise((resolve) => {
      //   setTimeout(() => {
      //     resolve({
      //       ok: true,
      //       json: () => Promise.resolve({ requestId: 'mock-request-id' }),
      //       // ok: false,
      //       // json: () => Promise.resolve({ message: "Something went wrong" }),
      //     });
      //   }, 5000);
      // });
      const data = await response.json();

      if (response.ok && data.requestId) {
        setRequestId(data.requestId);
        setStep('form');
        setError(null);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('Error creating recovery request:', error);
      setError('An error occurred, please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    let fixedFormData = {
      ...formData,
    };

    if (fixedFormData.botUsername && fixedFormData.botUsername.charAt(0) === '@') {
      fixedFormData.botUsername = fixedFormData.botUsername.substring(1);
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/account-recovery/submit-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...fixedFormData, requestId }),
      });
      const data = await response.json();

      if (response.ok && data.message) {
        setStep('verification');
        setError(null);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('An error occurred, please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/account-recovery/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ verificationCode: formData.verificationCode, requestId }),
      });
      const data = await response.json();

      if (response.ok && data.message) {
        setStep('success');
        setError(null);
      } else {
        setError(data.message);

        if (response.status === 409 || response.status === 410) {
          setIsTryAgain(true);
        }
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setError('An error occurred, please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecaptcha = (token) => {
    setCaptchaToken(token);
  }

  const onTryAgain = () => {
    setStep('captcha');
    setRequestId(null);
    setCaptchaToken(null);
    setFormData(defaultFormData);
    setTelegramUserFirstName(null);
    setTelegramUserLastName(null);
    setIsLoading(false);
    setIsTryAgain(false);
    setError(null);
  }

  return (
    <StaticLayout size={'big'} layoutCenter layoutFixed>
      <style>{`
        .form_group_recaptcha > div > div {
          display: flex;
          place-content: center;
        }
      `}</style>
      <div className="basis-0 grow shrink">
        <h1 className="text-[40px] md:text-[5.5rem] font-semibold text-brand-green mt-2 mb-[22px] md:mb-8 font-founders tracking-normal leading-none md:text-center max-w-[29rem] md:max-w-[35rem] mx-auto">
          InviteMember Account Recovery
        </h1>

        <div className="max-w-[29rem] md:max-w-[46rem] mx-auto">
          {step === 'captcha' && (
            <>
              <div className="md:text-center">
                <div>In case you lost the access to your Telegram account, here you can connect a new one.</div>
                <div>Your InviteMember subscription and projects will be transferred.</div>
                <div>To continue, please login with your new Telegram account first.</div>
              </div>
              <div className="form_group_recaptcha my-5" style={{ display: captchaToken ? 'none' : 'block' }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                  onChange={handleRecaptcha}
                  size="normal"
                />
              </div>
              {captchaToken && (
                <div className="max-w-[29rem] mx-auto">
                  <div className="border border-brand-green/[0.2] rounded-[16px] w-full px-6 py-[17px] mt-6 md:px-10 md:py-6.5 flex items-center flex-col justify-center mb-6 min-h-[76px]">
                    <TelegramLoginButton dataOnauth={onLogin} botName={process.env.REACT_APP_BOT_USERNAME} />
                  </div>
                </div>
              )}
            </>
          )}
          {step === 'form' && (
            <>
              <div className="md:text-center">
                You’re logged in as {(`${telegramUserFirstName || ''} ${telegramUserLastName || ''}`).trim()}. To continue, please complete the form.
              </div>
              <form onSubmit={handleFormSubmit} className="max-w-[29rem] mx-auto my-5">
                <div>
                  <Input
                    className="h-11 mb-6 md:mb-4"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  >
                    Email
                  </Input>
                </div>
                <div>
                  <Input
                    className="h-11 mb-6 md:mb-4"
                    type="text"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleInputChange}
                    required
                  >
                    Invoice Number
                  </Input>
                </div>
                <div>
                  <Input
                    className="h-11 mb-6 md:mb-4"
                    type="text"
                    name="last4Digits"
                    value={formData.last4Digits}
                    onChange={handleInputChange}
                    required
                    pattern="\d{4}"
                    minLength={4}
                    maxLength={4}
                  >
                    Last 4 digits of your card
                  </Input>
                </div>
                <div>
                  <Input
                    className="h-11 mb-6 md:mb-4"
                    type="text"
                    name="botUsername"
                    value={formData.botUsername}
                    onChange={handleInputChange}
                    required
                  >
                    Bot Username
                  </Input>
                </div>
                <div className="text-center">
                  <button
                    className="btn-primary mt-4 self-start w-auto min-w-32 md:mx-auto"
                    type="submit"
                    disabled={isLoading}
                  >
                    Submit
                  </button>
                </div>
              </form>
            </>
          )}
          {step === 'verification' && (
            <>
              <div className="md:text-center">
                <div>We’ve just sent the verification code to your email <b>{formData.email}</b>.</div>
                <div>Please enter the code here:</div>
              </div>
              <form onSubmit={handleVerificationSubmit} className="max-w-[29rem] mx-auto my-5">
                <div>
                  <Input
                    className="h-11 mb-6 md:mb-4"
                    type="text"
                    name="verificationCode"
                    value={formData.verificationCode}
                    onChange={handleInputChange}
                    minLength={6}
                    maxLength={6}
                    required
                    disabled={isTryAgain}
                  >
                    Verification Code
                  </Input>
                </div>
                <div className="text-center">
                  {isTryAgain ? (
                    <button
                      className="btn-primary mt-4 self-start w-auto min-w-32 md:mx-auto"
                      onClick={() => onTryAgain()}
                    >
                      Try again
                    </button>
                  ) : (
                    <button
                      className="btn-primary mt-4 self-start w-auto min-w-32 md:mx-auto"
                      type="submit"
                      disabled={isLoading}
                    >
                      Submit
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
          {step === 'success' && (
            <div className="text-center">
              <h2 className="h2 mb-4 pr-16 leading-[0.9] md:pr-0">
                You have successfully recovered your account.
              </h2>
              <div>You can now access your projects in InviteMember bot using your new Telegram account.</div>
              <a href="https://t.me/InviteMemberBot" className="btn-primary max-w-[268px] mx-auto mt-6 mb-3">@InviteMemberBot</a>
            </div>
          )}
          {isLoading && (
            <div className="text-brand-green w-[50px] h-[50px] my-1 mx-auto">
              <Loader />
            </div>
          )}
          {error && (
            <div className="text-sm text-red-orange text-center mt-2 font-medium">{error}</div>
          )}
        </div>
      </div>
    </StaticLayout>
  );
}
